import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import Configuration from './components/Configuration';
import CurrentValue from './components/CurrentValue';
import PatternDisplay from './components/PatternDisplay';
import Chart from './components/Chart';
import DigitFrequency from './components/DigitFrequency';
import Guide from './components/Guide';
import LoadingSpinner from './components/LoadingSpinner';
import { useWebSocket } from './hooks/useWebSocket';

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0.35rem;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  background: #ffffff;
  min-height: 100vh;
  color: #000000;
  line-height: 1.6;
  -webkit-font-smoothing: antialiased;
  position: relative;

  @media (max-width: 600px) {
    padding: 0.15rem;
  }
`;

const BackgroundWatermark = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%) rotate(-45deg);
  font-size: 120px;
  font-weight: bold;
  color: rgba(0, 0, 0, 0.03);
  z-index: 0;
  pointer-events: none;
  user-select: none;
  white-space: nowrap;

  @media (max-width: 768px) {
    font-size: 60px;
  }

  @media (max-width: 480px) {
    font-size: 40px;
  }
`;

const ContentWrapper = styled.div`
  position: relative;
  z-index: 1;
`;


const ErrorMessage = styled.div`
  background-color: #fef2f2;
  border: 1px solid #fecaca;
  color: #dc2626;
  padding: 1rem;
  border-radius: 0.5rem;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const LoadingOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const Row = styled.div`
  display: flex;
  justify-content: center;
`;

const Col = styled.div`
  width: 100%;
`;

const allowedDomains = [
  'https://dbot.githinji.site/',
  'https://www.dbtraders.com/',
  'https://www.dbotspace.com/',
  'https://www.mrduke.site/',
  'https://www.mkoreanwwn.com/',
  'https://dbot.dollarprinterhub.site/',
  'https://dbot.mrduke.site/',
  'https://www.dinsightstool.com/',
  'https://www.d-aitool.com/',
  'https://app.guruscripts.com/',
  'https://dbot.mkoreanwwn.com/',
  'https://www.bucksradar.com/',
  'https://www.marketmavenfx.site/',
  'https://www.profitmaster.site/',
  'https://www.vinnyfx.site/',
  'https://www.tradewithscottfx.site/',
  'https://www.binaryguru.site/',
  'https://www.kikuyufx.site/',
  'https://www.darictrader.site/',

  // ⭐ Newly added domains
  'https://dbot.binarytool.site/',
  'https://www.smartdollarhub.site/',
  'https://www.bmbtraders.site/',
  'https://alphatraders.vercel.app/',
  'https://www.marketsniper.site/',
  'https://www.tickflow.site/',
  'https://mrdollars.tickflow.site/',
  'https://www.dtraderscore.com/',
  'https://bot.frankfxx.com/',
  'https://www.derexhub.site/',
  'https://www.primetrades.net/',
  'https://forexalmighty.vercel.app/',
  'https://www.kingsfx.site/',
  'https://www.kashytrader.site/',
  'https://www.marketscalper.site/',
  'https://www.fametraders.site/',
  'https://jeffhub.binarytool.site/',
  'https://random.binarytool.site/',
  'https://siralex.binarytool.site/',
  'https://www.binaryedge.site/',
  'https://www.ikiihub.site/',
  'https://app.sammykhizz.site/',
  'https://dbot.dollarprinterhub.site/',
  'https://www.succeedtool.com/',
  'https://www.primeanalyser.com/',
  'https://spallis.copytradingsite.com/',
  'https://states.copytradingsite.com/',
  'https://dfire.copytradingsite.com/',
  'https://jose.copytradingsite.com/',
  'https://tangi.copytradingsite.com/',
  'https://spongebobfx.copytradingsite.com/',
  'https://brandy.copytradingsite.com/',
  'https://www.dbotspace.com/',
  'https://app.dbotspace.com/',
  'https://www.lady-p.site/',
  'https://copytrading.dbtraders.com/',
  'https://www.pipsdollarprinter.site/',
  'https://www.mastertool.site/',
  'https://www.tradehint.site/',
  'https://www.mrduke.site/', // duplicate removed earlier but keeping final
  'https://dbot.ultimatedigits.ai/',
  'https://www.epicstraders.site/',
  'https://www.tradermathews.com/',
  'https://www.dollarflow.site/',
  'https://www.d-alpha.site/',
  'https://www.dollarpath.site/',
  'https://www.tradenexa.site/',
  'https://www.alphatraders.site/',
  'https://fjnvxnv8-8443.inc1.devtunnels.ms/',
  'https://www.paigeytraders.com/',
  'https://www.dtradersfreebot.site/'
];


function App() {
  const [market, setMarket] = useState('R_10');
  const [contractType, setContractType] = useState('0');
  const [ticks, setTicks] = useState(120);
  const [overValue, setOverValue] = useState(4);
  const [underValue, setUnderValue] = useState(5);

  const referrer = document.referrer;
  const isPremium = allowedDomains.some(domain => referrer.startsWith(domain));
  const title = isPremium ? 'Premium Analysistool' : 'DERIV Analysistool';

  useEffect(() => {
    console.log('Document referrer:', referrer);
    console.log('Is premium:', isPremium);
    console.log('Header title set to:', title);
  }, [isPremium, referrer, title]);

  const { data, isLoading, error } = useWebSocket({
    market,
    contractType,
    ticks,
    overValue,
    underValue,
  });

  const handleMarketChange = (newMarket: string) => {
    setMarket(newMarket);
  };

  const handleContractTypeChange = (newType: string) => {
    setContractType(newType);
  };

  const handleTicksChange = (newTicks: number) => {
    setTicks(newTicks);
  };

  const handleOverUnderChange = (over: number, under: number) => {
    setOverValue(over);
    setUnderValue(under);
  };

  // Disable right-click context menu and dev tools
  useEffect(() => {
    const handleContextMenu = (e: MouseEvent) => {
      e.preventDefault();
      return false;
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      // Disable F12, Ctrl+Shift+I, Ctrl+Shift+J, Ctrl+Shift+C, Ctrl+U
      if (
        e.keyCode === 123 || // F12
        (e.ctrlKey && e.shiftKey && (e.keyCode === 73 || e.keyCode === 74 || e.keyCode === 67)) || // Ctrl+Shift+I/J/C
        (e.ctrlKey && e.keyCode === 85) // Ctrl+U
      ) {
        e.preventDefault();
        return false;
      }
    };

    // Disable context menu
    document.addEventListener('contextmenu', handleContextMenu);

    // Disable keyboard shortcuts
    document.addEventListener('keydown', handleKeyDown);

    // Cleanup
    return () => {
      document.removeEventListener('contextmenu', handleContextMenu);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  return (
    <Container>
      <BackgroundWatermark>binarytool.site</BackgroundWatermark>

      <ContentWrapper>
        <Row>
          <Col>

            {error && (
              <ErrorMessage>
                <i className="bi bi-exclamation-triangle"></i>
                <div>
                  <strong>Connection Error:</strong> {error}
                  <br />
                  <small>Please check your internet connection and try again.</small>
                </div>
              </ErrorMessage>
            )}

            <Configuration
              market={market}
              contractType={contractType}
              ticks={ticks}
              overValue={overValue}
              underValue={underValue}
              onMarketChange={handleMarketChange}
              onContractTypeChange={handleContractTypeChange}
              onTicksChange={handleTicksChange}
              onOverUnderChange={handleOverUnderChange}
            />

            <CurrentValue
              currentValue={data.currentValue}
            />

            <DigitFrequency
              percentages={data.digitPercentages}
              currentDigit={Number(data.currentValue.slice(-1))}
            />

            <PatternDisplay pattern={data.pattern} contractType={contractType} />

            <Chart
              data={data.chartData}
              contractType={contractType}
            />

            <Guide contractType={contractType} />

            {isLoading && (
              <LoadingOverlay>
                <LoadingSpinner message="Loading market data..." />
              </LoadingOverlay>
            )}
          </Col>
        </Row>
      </ContentWrapper>
    </Container>
  );
}

export default App;
